import {
  FC, ReactNode, useEffect, useRef,
} from 'react'
import { createPortal } from 'react-dom'
import cn from 'classnames'

/**
 * The app's own modal, replacing react-bootstrap's.
 *
 * The sub-component API is kept — `Modal.Header` / `Modal.Body` / `Modal.Footer` —
 * because 56 files render those directly, 85 JSX sites in all. Keeping the shape
 * means the migration is an import change in those files rather than a rewrite of
 * every one, and the class names it emits are the ones 44 rules across the app's
 * SCSS already target.
 *
 * The DOM matches what react-bootstrap produced, verified against the behaviour
 * contract in tests/unit/components/UI/Modals/ModalFrame.test.tsx:
 *
 *   <div role="dialog" aria-modal="true" tabindex="-1" class="fade modal show">
 *     <div class="modal-dialog {dialogClassName} modal-dialog-centered">
 *       <div class="modal-content {contentClassName}"> … </div>
 *
 * Deliberately NOT reproduced:
 *   - backdrop click to close. Every call site takes `backdrop: 'static'`, so the
 *     app has never had it, and the contract pins that it stays that way.
 *   - Popper/positioning helpers. Modals are centred by CSS, not measured.
 *   - the body `padding-right` compensation. react-bootstrap adds it to offset a
 *     scrollbar it expects `.modal-open` to remove — but Bootstrap 5 dropped that
 *     rule, so today the page is shifted without ever being locked. This sets the
 *     lock instead, which is what the compensation was for.
 *
 * The utility lists below carry what `shared/_modal_layout.scss` used to declare
 * from a hoisted `@layer base`, ahead of every utility. The scroller, backdrop,
 * centred dialog and body took plain utilities. The dialog's margin / max-width and
 * the content, header and footer boxes are `[:where(&)]:` utilities instead: 0-0-0,
 * so any class a caller or a page rule sets on the element still overrides them, as
 * it overrode the layer. Several of their declarations read `--bs-modal-*` variables
 * defined nowhere; they are kept verbatim because an invalid border shorthand still
 * resets that side's border-style to `none`.
 *
 * TWO CONSEQUENCES FOR THIS COMPONENT'S OWN API, because the plain ones are now
 * 0-1-0 utilities on the element instead of 0-1-0 rules in a hoisted layer:
 *   - `dialogClassName` (70+ call sites) and `ModalBody`'s `className` (60+) used to
 *     WIN against the layer rules for display, align-items, position, min-height and
 *     the flex shorthand. They no longer do: these utilities are emitted later in
 *     the same band, so the component now beats the caller for those five
 *     properties. The margins are thin and are emission order, not specificity —
 *     e.g. the flex-1 utility is emitted just before flex-auto. `cn` here is
 *     `classnames`, not the twMerge-backed helper, so nothing de-duplicates them.
 *   - no current call site passes a competing class; this was checked across every
 *     dialogClassName, contentClassName and ModalBody className in src.
 *
 * The class names STAY on the DOM. Other SCSS rules target them,
 * tests/e2e/style/routes.ts waits on several of them, and three hand-written modals
 * under container/workflow-application-edit/custom-domain-input write
 * `.modal-content` / `.modal-header` / `.modal-footer` themselves — which is why the
 * three class lists below are exported and applied there too.
 */

/**
 * Every `.modal-content`'s look and per-theme gray re-pin (light differs from the page's),
 * shared with the three hand-written custom-domain boxes. Override the look with `[&]:` or
 * `!`; the `[:where(&)]:` tail (Bootstrap's structural box) yields to any class.
 */
export const MODAL_CONTENT_CLASSES = 'rounded-xl [border:none] bg-gray-6-filled text-gray-1 '
  + '[[data-theme=light]_&]:[--gray-1:#1A1A1A] [[data-theme=light]_&]:[--gray-2:rgba(0,0,0,_0.70)] '
  + '[[data-theme=light]_&]:[--gray-3:rgba(0,0,0,_0.50)] [[data-theme=light]_&]:[--gray-4:rgba(0,0,0,_0.30)] '
  + '[[data-theme=light]_&]:[--gray-4-filled:#B3B3B3] [[data-theme=light]_&]:[--gray-5:rgba(0,0,0,_0.10)] '
  + '[[data-theme=light]_&]:[--gray-6:rgba(0,0,0,_0.03)] [[data-theme=light]_&]:[--gray-6-filled:#FFFFFF] '
  + '[[data-theme=light]_&]:[--gray-7:#F7F7F7] '
  + '[[data-theme=dark]_&]:[--gray-1:#FFFFFF] [[data-theme=dark]_&]:[--gray-2:rgba(255,255,255,_0.70)] '
  + '[[data-theme=dark]_&]:[--gray-3:rgba(255,255,255,_0.50)] [[data-theme=dark]_&]:[--gray-4:rgba(255,255,255,_0.30)] '
  + '[[data-theme=dark]_&]:[--gray-4-filled:#5F5E5D] [[data-theme=dark]_&]:[--gray-5:rgba(255,255,255,_0.10)] '
  + '[[data-theme=dark]_&]:[--gray-6:rgba(255,_255,_255,_0.05)] [[data-theme=dark]_&]:[--gray-6-filled:#262523] '
  + '[[data-theme=dark]_&]:[--gray-7:#1A1917] '
  + '[:where(&)]:relative [:where(&)]:flex [:where(&)]:flex-col [:where(&)]:w-full [:where(&)]:pointer-events-auto '
  + '[:where(&)]:bg-clip-padding [:where(&)]:[outline:0]'

/** Bootstrap's `.modal-header` box, shared with the three hand-written custom-domain headers. */
export const MODAL_HEADER_CLASSES = '[:where(&)]:flex [:where(&)]:shrink-0 [:where(&)]:items-center '
  + '[:where(&)]:[padding:var(--bs-modal-header-padding)] '
  + '[:where(&)]:[border-bottom:var(--bs-modal-header-border-width)_solid_var(--bs-modal-header-border-color)] '
  + '[:where(&)]:[border-top-left-radius:var(--bs-modal-inner-border-radius)] '
  + '[:where(&)]:[border-top-right-radius:var(--bs-modal-inner-border-radius)]'

/** Bootstrap's `.modal-footer` box, shared with the three hand-written custom-domain footers. */
export const MODAL_FOOTER_CLASSES = '[:where(&)]:flex [:where(&)]:shrink-0 [:where(&)]:flex-wrap [:where(&)]:items-center '
  + '[:where(&)]:justify-end [:where(&)]:[padding:calc(var(--bs-modal-padding)_-_var(--bs-modal-footer-gap)*.5)] '
  + '[:where(&)]:[background-color:var(--bs-modal-footer-bg)] '
  + '[:where(&)]:[border-top:var(--bs-modal-footer-border-width)_solid_var(--bs-modal-footer-border-color)] '
  + '[:where(&)]:[border-bottom-right-radius:var(--bs-modal-inner-border-radius)] '
  + '[:where(&)]:[border-bottom-left-radius:var(--bs-modal-inner-border-radius)]'

type ModalSectionProps = { className?: string, children?: ReactNode }

// Sections: `[:where(.modal-content)_&]:` is 0-1-0 emitted after all app SCSS and `[&]:`,
// so a 0-2-0 rule, `[&.modal-body]:` or `!` overrides it and a plain or `[&]:` utility
// does not. The h1 and `>*` variants reach children the callers render.
const Header: FC<ModalSectionProps> = ({ className, children }) => (
  <div className={cn(className, 'modal-header', MODAL_HEADER_CLASSES, '[:where(.modal-content)_&]:flex [:where(.modal-content)_&]:items-center [:where(.modal-content)_&]:[border-bottom:none] [:where(.modal-content)_&_h1]:mb-0')}>{children}</div>
)

const Body: FC<ModalSectionProps> = ({ className, children }) => (
  // `relative flex-auto` are the two live declarations of the old `.modal-body` rule;
  // its `padding: var(--bs-modal-padding)` read a variable defined nowhere. See the
  // caller-contract note in the docblock above. The p+p variant keeps the old rule's
  // 0-2-2: this is the only element that carries `.modal-body`.
  <div className={cn(className, 'modal-body', 'relative flex-auto', '[:where(.modal-content)_&]:[border-top:1px_solid_var(--gray-5)] [:where(.modal-content)_&]:[border-bottom:1px_solid_var(--gray-5)] [:where(.modal-content)_&]:p-[24px]', '[.grid-small-modal-content_&_p+p]:mt-[24px]')}>{children}</div>
)

const Footer: FC<ModalSectionProps> = ({ className, children }) => (
  <div className={cn(className, 'modal-footer', MODAL_FOOTER_CLASSES, '[:where(.modal-content)_&]:p-[24px] [:where(.modal-content)_&]:[border-top:none] [:where(.modal-content)_&>*]:m-0')}>{children}</div>
)

type ModalProps = {
  show?: boolean
  onHide?: () => void
  centered?: boolean
  animation?: boolean
  backdrop?: boolean | 'static'
  /** Whether Escape closes the modal; turned off for content that handles Escape itself. */
  keyboard?: boolean
  dialogClassName?: string
  contentClassName?: string
  /** Id of the element that names the dialog for screen readers. */
  'aria-labelledby'?: string
  children?: ReactNode
}

type ModalComponent = FC<ModalProps> & {
  Header: typeof Header
  Body: typeof Body
  Footer: typeof Footer
}

const ModalBase: FC<ModalProps> = ({
  show = false, onHide, centered = false, animation = true,
  backdrop = 'static', keyboard = true, dialogClassName, contentClassName, children, 'aria-labelledby': labelledBy,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null)
  const restoreTo = useRef<Element | null>(null)

  // `onHide` is read through a ref so it can stay OUT of the effect's dependency
  // list. 49 of the 78 call sites pass an inline arrow — `onHide={() => setOpen(false)}`
  // — which is a new identity on every render. With `onHide` as a dependency, typing
  // one character into a field inside the modal re-ran this effect, and
  // `dialogRef.current?.focus()` pulled focus off the input and onto the dialog. The
  // cleanup's `restoreTo.current.focus()` compounded it. Every keystroke cost a click.
  const onHideRef = useRef(onHide)
  useEffect(() => { onHideRef.current = onHide })
  const keyboardRef = useRef(keyboard)
  useEffect(() => { keyboardRef.current = keyboard })

  useEffect(() => {
    if (!show) return undefined

    restoreTo.current = document.activeElement
    dialogRef.current?.focus()

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.body.classList.add('modal-open')

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && keyboardRef.current) onHideRef.current?.()
    }
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      document.body.classList.remove('modal-open')
      // Put focus back where it was; without this it falls to <body> and the
      // keyboard user loses their place.
      if (restoreTo.current instanceof HTMLElement) restoreTo.current.focus()
    }
    // `show` only: see the ref above.
  }, [show])

  if (!show || typeof document === 'undefined') return null

  return createPortal(
    <>
      {/* The colour is written as an arbitrary PROPERTY, not an arbitrary value:
          the value form compiles to an opacity custom property plus an rgb()
          reference, which computes the same but sets a variable on the element
          that no capture could see. Nothing else in the compiled sheet mentions
          this class. */}
      <div className={cn({ fade: animation }, 'modal-backdrop show', 'fixed left-0 top-0 z-[1050] h-screen w-screen [background-color:#000] opacity-50')} />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        // The min-width:576px variant on the margin variable is load-bearing, not
        // tidiness: the plain custom-property utility lands at 56,864, so an
        // override left behind in SCSS at 13,819 would lose at every width and
        // every dialog would sit 8px from the edge instead of 28px. The variant
        // compiles into the variant band and wins there. 576px is not a Tailwind
        // breakpoint (`sm` is 640px), hence the arbitrary form. The focus-ring
        // reset is spelled as an arbitrary property: Tailwind's named form emits a
        // visible 2px indicator.
        className={cn({ fade: animation }, 'modal show', '[--bs-modal-zindex:1055] [--bs-modal-width:500px] [--bs-modal-margin:0.5rem] min-[576px]:[--bs-modal-margin:1.75rem] fixed left-0 top-0 z-[var(--bs-modal-zindex)] h-full w-full overflow-y-auto overflow-x-hidden [outline:0]')}
        style={{ display: 'block' }}
        // Escape is handled once, by the document listener above; a second handler here closed every modal twice.
        // `backdrop: 'static'` everywhere, so a click on the scroller does not
        // dismiss. Kept explicit so the intent is readable rather than implied.
        onClick={backdrop === true && onHide ? e => {
          if (e.target === e.currentTarget) onHide()
        } : undefined}
      >
        {/* The centred utilities ride the SAME `centered` flag as the class, so they
            land on exactly the elements the old rule matched. The margin and the
            576px max-width are `[:where(&)]:` (0-0-0), so any dialogClassName class
            still beats them; the 576px pair is emitted after the base margin, which is
            what lets `auto` centre the dialog. */}
        {/* `.modal-content .modal-header { padding: 24px }`, at the same 0-2-0: this
            dialog's only child is `.modal-content`, so it reaches the same headers. */}
        <div className={cn('modal-dialog', 'relative pointer-events-none', '[:where(&)]:m-[var(--bs-modal-margin)] min-[576px]:[:where(&)]:max-w-[var(--bs-modal-width)] min-[576px]:[:where(&)]:mx-auto', dialogClassName, { 'modal-dialog-centered': centered }, centered && 'flex items-center min-h-[calc(100%-var(--bs-modal-margin)*2)]', '[&_.modal-header]:p-6')}>
          <div className={cn('modal-content', MODAL_CONTENT_CLASSES, contentClassName)}>
            {children}
          </div>
        </div>
      </div>
    </>,
    document.body,
  )
}

const Modal = ModalBase as ModalComponent
Modal.Header = Header
Modal.Body = Body
Modal.Footer = Footer

export default Modal
